/**
 * Session + patient-link contract.
 *
 * The shape here is what kills the spike helper's "any channel name, at any
 * time" endpoint (ADR-0007 §2). In the spike, possession of an arbitrary string
 * was enough to mint a publishing RTC token. Here the link token is a real
 * capability: it resolves to a specific session, and that session's liveness and
 * expiry are checked *before* any credential is minted.
 */

import { z } from 'zod';
import { OPERATED_SIDES } from './episode';
import type { SessionId } from './ids';

/**
 * Lifecycle. `pending` exists because a physio creates the session and shares
 * the link before opening the call — a patient who taps early must be told to
 * wait, not told the link is broken.
 *
 * `dropped` (D4) is system-detected, never client-sent: the API's drop
 * sweeper appends it when a live session shows no recorded activity past a
 * timeout — the record for an unclean exit (crash, network loss, battery)
 * instead of `live` forever. A dropped session may still resume (`start` →
 * live, a true recorded history) or close (`end` → ended). Only `ended` is
 * terminal.
 */
export const SESSION_STATUSES = ['pending', 'live', 'ended', 'dropped'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/**
 * Every verdict linkState (below) can reach. `ok` is the joinable case; the
 * other three are refusals, each of which is also an ErrorCode so the join
 * route can report exactly what resolve predicted. Declared up here because
 * resolveLinkResponseSchema consumes it at module-init time.
 */
export const LINK_STATE_REASONS = [
  'ok',
  'session_ended',
  'link_expired',
  'session_not_live',
] as const;
export type LinkStateReason = (typeof LINK_STATE_REASONS)[number];

/**
 * Opaque patient capability. 32 random bytes, base64url — not derived from the
 * session id, so neither can be guessed from the other, and the id never travels
 * in a URL that gets pasted into chat apps.
 */
export const LINK_TOKEN_BYTES = 32;
export const linkTokenSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/, 'malformed link token')
  .brand<'LinkToken'>();
export type LinkToken = z.infer<typeof linkTokenSchema>;

/** Agora channel name. Agora's own limit is 64 chars from a restricted set. */
export const channelSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'malformed channel');

/**
 * C9 vocabulary — the bound dataset's third status is completed-early-stop.
 * Declared before sessionSchema, which consumes it.
 *
 * SC6 (ADR-0032) adds a fourth value, `incomplete-technical` — the day
 * closed for technical reasons, not a physio's decision. Name is OQ-1, still
 * open; every reference to the string lives behind this one constant so a
 * rename touches one file plus the migration that mirrors it in the DB
 * CHECK (026_incomplete_technical_disposition.sql).
 *
 * It is deliberately NOT in `PHYSIO_END_DISPOSITIONS` below: it is written
 * by the system alone (the sweeper, SC6), never chosen by a physio's own
 * end-of-call flow — "she is never asked to classify her own crash" is a
 * schema fact, not just a UI one.
 */
export const SESSION_END_DISPOSITIONS = [
  'completed',
  'early-stop-safety',
  'incomplete-technical',
] as const;
export type SessionEndDisposition = (typeof SESSION_END_DISPOSITIONS)[number];

/** What a physio may submit at POST /api/sessions/:id/end — see the note above. */
export const PHYSIO_END_DISPOSITIONS = ['completed', 'early-stop-safety'] as const;
export type PhysioEndDisposition = (typeof PHYSIO_END_DISPOSITIONS)[number];

export const sessionSchema = z.object({
  id: z.uuid(),
  channel: channelSchema,
  status: z.enum(SESSION_STATUSES),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  /** After this instant the link stops resolving, regardless of status. */
  linkExpiresAt: z.iso.datetime(),
  /**
   * Whether this session carries recording consent (C8) — an immutable
   * creation fact: a session cannot "gain" consent later; a newly consented
   * session is a new session. Since the episode substrate landed (A2/A3,
   * migration 012) this IS a projection: the server derives it at creation
   * from the episode's consent head (recording consent AND the ADR-0004
   * full-retention acknowledgment) and no client can assert it (TM-07).
   */
  recordingConsent: z.boolean(),
  /**
   * The episode this session belongs to. Null only on legacy pre-spine dev
   * rows (012 lands the column nullable, no backfill); every session created
   * since requires one.
   */
  episodeId: z.uuid().nullable(),
  /**
   * The calendar slot this session was bound to at creation (A4/B2), or null
   * for an ad-hoc session (the A4-pin manual path). Binding happens ONLY at
   * creation and only to today's slot — that construction is what makes a
   * missed slot permanently missed.
   */
  slotId: z.uuid().nullable().optional(),
  /**
   * C9 — how the session ended: the physio's entered decision at the End
   * flow, never inferred, never auto-raised. Null while the session is not
   * ended, and on legacy ends that predate the grammar.
   */
  endDisposition: z.enum(SESSION_END_DISPOSITIONS).nullable().optional(),
  /** The physio's entered reason; present exactly when the stop was for safety. */
  endDispositionReason: z.string().nullable().optional(),
});
export type Session = z.infer<typeof sessionSchema> & { readonly id: SessionId };

// ---- requests / responses --------------------------------------------------

/**
 * POST /api/sessions — physio creates a session and gets a shareable link.
 *
 * There is deliberately NO consent field here (TM-07, closed by A3): the
 * increment-2 placeholder let the client assert recording consent, and the
 * server stopped trusting that the moment patient-captured episode consent
 * existed. The server derives the session's consent fact from the episode's
 * consent head; a client-sent `recordingConsent` key is stripped by parsing,
 * never read.
 */
/**
 * B1 — one pre-session plan tweak (delta-only; dosage within the prototype's
 * own bounds) WITH its required rationale. Paused = deliberately not in
 * today's plan; a paused tweak carries no dosage.
 */
export const planTweakSchema = z
  .object({
    exerciseCode: z.string().min(1).max(16),
    paused: z.boolean().default(false),
    sets: z.number().int().min(1).max(4).optional(),
    reps: z.number().int().min(3).max(10).optional(),
    /** Why today's plan differs — required, stored as data (PRD B1). */
    rationale: z.string().trim().min(1).max(500),
  })
  .superRefine((t, ctx) => {
    if (t.paused && (t.sets !== undefined || t.reps !== undefined)) {
      ctx.addIssue({ code: 'custom', message: 'a paused exercise carries no dosage' });
    }
    if (!t.paused && t.sets === undefined && t.reps === undefined) {
      ctx.addIssue({ code: 'custom', message: 'a tweak tweaks something' });
    }
  });
export type PlanTweak = z.infer<typeof planTweakSchema>;

export const createSessionRequestSchema = z.object({
  /** The episode this session belongs to. Required — sessions without a patient spine no longer exist. */
  episodeId: z.uuid(),
  /**
   * Today's calendar slot to bind (A4). Omitted = ad-hoc session (A4-pin).
   * The server refuses any slot that is not the episode's own, not a head
   * row, or not dated TODAY (IST) — binding at creation is B2's construction.
   */
  slotId: z.uuid().optional(),
  /**
   * B1 — today's plan deltas vs the pinned protocol, decided BEFORE the
   * session and written with it (the plan of record is a creation fact).
   * Codes must exist in the episode's pinned template; one tweak per code.
   */
  planTweaks: z
    .array(planTweakSchema)
    .max(10)
    .optional()
    .refine(
      (tweaks) => !tweaks || new Set(tweaks.map((t) => t.exerciseCode)).size === tweaks.length,
      'one tweak per exercise',
    ),
  /** Minutes the patient link stays valid. Bounded so a stale link cannot live forever. */
  linkTtlMinutes: z
    .number()
    .int()
    .min(5)
    .max(24 * 60)
    .default(120),
});
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;
/**
 * The CALLER-side shape of the same contract (defaults still optional).
 * Clients type their request against this and send it wholesale — RT#9's F8
 * came from a client hand-building a subset of the body, which no compiler
 * checked because excess-property checking skips spread-built call sites.
 */
export type CreateSessionRequestInput = z.input<typeof createSessionRequestSchema>;

export const createSessionResponseSchema = z.object({
  ok: z.literal(true),
  session: sessionSchema,
  linkToken: linkTokenSchema,
  /** Fully-qualified URL to hand to the patient. */
  patientUrl: z.url(),
  /**
   * The episode's operative side (A2), served with the session so in-call
   * laterality surfaces (the D5 per-exercise angle chip, register C5) bind
   * to the operated limb — server truth, same TM-07 posture as
   * recordingConsent. Optional on the wire only for skew tolerance (a server
   * predating increment-7 PR8 sends none); current servers always send it.
   */
  operatedSide: z.enum(OPERATED_SIDES).optional(),
});
export type CreateSessionResponse = z.infer<typeof createSessionResponseSchema>;

/**
 * GET /api/link/:linkToken — resolve a link WITHOUT minting credentials.
 *
 * Split from join on purpose: the patient page needs to render "not started
 * yet" or "expired" before asking for camera permission. Minting an RTC token
 * just to discover the session is not live would burn a credential and, worse,
 * would make an expired link indistinguishable from a broken one.
 */
export const resolveLinkResponseSchema = z.object({
  ok: z.literal(true),
  status: z.enum(SESSION_STATUSES),
  sessionId: z.uuid(),
  /** Whether joining is possible right now. False for pending and ended alike. */
  joinable: z.boolean(),
  /**
   * WHY joining is (im)possible — linkState's verdict, verbatim (D9). Without
   * it a non-joinable resolve is ambiguous: an expired link on a pending
   * session would read as "not started yet" and wait forever. The server is
   * the only party that can order expiry against status truthfully, so the
   * reason travels with the resolve instead of being re-derived client-side.
   */
  reason: z.enum(LINK_STATE_REASONS),
  /**
   * SC1/ADR-0032 — set while a `live` session has a patient witness signal
   * (SC3) on record and the five-minute recovery window it opens has not yet
   * elapsed: the fixed instant the patient browser's countdown counts down
   * to, so the countdown is drawn from the server's clock, never a client
   * guess. Null whenever there is nothing to recover from. Optional on the
   * wire only for skew tolerance (operatedSide's precedent above) — a server
   * predating session continuity sends none; current servers always send it.
   */
  recoveryDeadline: z.iso.datetime().nullable().optional(),
});
export type ResolveLinkResponse = z.infer<typeof resolveLinkResponseSchema>;

/**
 * POST /api/link/:linkToken/witness — SC3: the patient browser's fast,
 * unauthenticated report that the physio's video track disappeared. The link
 * token is the caller's only credential (it holds no other); `occurredAt` is
 * the patient device's own clock at the moment of detection and is carried
 * for display ordering ONLY — the server stamps its own receive time as the
 * evidence a device with a wrong clock cannot forge (session_liveness,
 * migration 025).
 */
export const witnessRequestSchema = z.object({
  occurredAt: z.iso.datetime(),
});
export type WitnessRequest = z.infer<typeof witnessRequestSchema>;

export const witnessResponseSchema = z.object({ ok: z.literal(true) });
export type WitnessResponse = z.infer<typeof witnessResponseSchema>;

/**
 * POST /api/sessions/:sessionId/heartbeat — SC2: the physio app's "still
 * here", sent every 30 s while it is in a call for a `live` session. Its
 * ABSENCE is the signal the sweeper's armed clock (SC4) arms on — this route
 * itself never blocks anything and never fails the call.
 *
 * `appVersion` is not in the spec's own API-surface table (§6) but rides
 * along here because `session_liveness.heartbeat_app_version` exists for
 * exactly the debug-trail line SC8 illustrates ("app 1.4.2") and the client
 * already has it on hand (`expo-application`) — optional, so an older build
 * that predates this field still heartbeats cleanly.
 */
export const heartbeatRequestSchema = z.object({
  appRunId: z.string().min(1),
  appVersion: z.string().min(1).optional(),
});
export type HeartbeatRequest = z.infer<typeof heartbeatRequestSchema>;

/** POST /api/link/:linkToken/join — resolve, check liveness, then mint. */
export const joinSessionRequestSchema = z.object({
  /** Client-chosen Agora uid. 0 lets Agora assign one. */
  uid: z.number().int().min(0).max(0xffffffff).default(0),
});
export type JoinSessionRequest = z.infer<typeof joinSessionRequestSchema>;

/**
 * POST /api/sessions/:sessionId/end — C9: closing the call RECORDS the
 * disposition. Required on every end; a reason exactly when the stop was for
 * safety. Never defaulted — a default would be an inference.
 *
 * `PHYSIO_END_DISPOSITIONS`, not the full `SESSION_END_DISPOSITIONS` — a
 * physio's own end can never claim `incomplete-technical` (SC6): that value
 * is system-written only, by the sweeper, on a session the physio never
 * chose to close this way.
 */
export const endSessionRequestSchema = z
  .object({
    disposition: z.enum(PHYSIO_END_DISPOSITIONS),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .superRefine((r, ctx) => {
    if (r.disposition === 'early-stop-safety' && !r.reason) {
      ctx.addIssue({ code: 'custom', message: 'an early stop records why', path: ['reason'] });
    }
    if (r.disposition === 'completed' && r.reason) {
      ctx.addIssue({
        code: 'custom',
        message: 'a completed session carries no stop reason',
        path: ['reason'],
      });
    }
  });
export type EndSessionRequest = z.infer<typeof endSessionRequestSchema>;

/** POST /api/sessions/:sessionId/start | /end — physio opens and closes the call. */
export const sessionStatusResponseSchema = z.object({
  ok: z.literal(true),
  session: sessionSchema,
});
export type SessionStatusResponse = z.infer<typeof sessionStatusResponseSchema>;

/**
 * GET /api/sessions/active — SC7: "which session was I in?", answered
 * authoritatively (survives an app-data wipe; a locally-persisted sessionId
 * is only ever the fast path on top of this). Null when the signed-in
 * physio has nothing `live` or `dropped` unresumed. OQ-8 (what to answer
 * when more than one exists) is still open; today's answer is newest wins —
 * the actor of the most recent live-or-dropped transition, one row, never a
 * list, which is a strict subset of whatever OQ-8 eventually rules.
 */
export const activeSessionResponseSchema = z.object({
  ok: z.literal(true),
  session: sessionSchema.nullable(),
});
export type ActiveSessionResponse = z.infer<typeof activeSessionResponseSchema>;

// ---- pure helpers ----------------------------------------------------------

/**
 * The single liveness rule, shared by the API and both clients so a client can
 * render the right state without a round-trip and cannot disagree with the
 * server about what "joinable" means.
 */
export function linkState(
  session: Pick<Session, 'status' | 'linkExpiresAt'>,
  now: Date = new Date(),
): { joinable: boolean; reason: LinkStateReason } {
  if (session.status === 'ended') return { joinable: false, reason: 'session_ended' };
  // A dropped session reads as over to the patient — deliberately the same
  // reason as `ended` so no client needs new copy; the record keeps the
  // distinction. If the physio resumes (start → live), the link works again.
  if (session.status === 'dropped') return { joinable: false, reason: 'session_ended' };
  if (new Date(session.linkExpiresAt).getTime() <= now.getTime()) {
    return { joinable: false, reason: 'link_expired' };
  }
  if (session.status === 'pending') return { joinable: false, reason: 'session_not_live' };
  return { joinable: true, reason: 'ok' };
}
